import React, { useState } from 'react';
import axios from 'axios';
import { Handshake, Loader2, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { company_name: '', contact_name: '', email: '', phone: '', message: '' };

const errorText = (detail) => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return 'Verifique os campos: email válido e mensagem com pelo menos 5 caracteres.';
  return 'Não foi possível enviar. Tente novamente.';
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);

const inputClass = 'w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#10b981] focus:ring-2 focus:ring-[#10b981]/20 outline-none transition-colors';

export const PartnershipDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      await axios.post(`${API}/partnerships`, form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      setError(errorText(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const handleOpenChange = (value) => {
    onOpenChange(value);
    if (!value) { setSent(false); setError(''); }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="partnership-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Handshake className="w-6 h-6 text-[#10b981]" /> Seja Parceiro TugGo
          </DialogTitle>
          <DialogDescription>
            Conte-nos sobre a sua empresa. A nossa equipa responde em breve.
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="text-center py-8" data-testid="partnership-success">
            <CheckCircle className="w-14 h-14 text-[#10b981] mx-auto mb-4" />
            <p className="text-lg font-semibold text-slate-900">Pedido enviado com sucesso!</p>
            <p className="text-slate-600 mt-1">Entraremos em contacto consigo muito em breve.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="partnership-form">
            <Field label="Nome da empresa *">
              <input required minLength={2} value={form.company_name} onChange={update('company_name')} className={inputClass} data-testid="partnership-company" />
            </Field>
            <Field label="Pessoa de contacto *">
              <input required minLength={2} value={form.contact_name} onChange={update('contact_name')} className={inputClass} data-testid="partnership-contact" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Email *">
                <input required type="email" value={form.email} onChange={update('email')} className={inputClass} data-testid="partnership-email" />
              </Field>
              <Field label="Telemóvel">
                <input type="tel" value={form.phone} onChange={update('phone')} className={inputClass} data-testid="partnership-phone" />
              </Field>
            </div>
            <Field label="Como gostaria de colaborar? *">
              <textarea required minLength={5} rows={4} value={form.message} onChange={update('message')} className={inputClass} placeholder="Ex.: entregas diárias para a nossa loja em Lisboa" data-testid="partnership-message" />
            </Field>
            {error && <p className="text-sm text-red-600" data-testid="partnership-error">{error}</p>}
            <button
              type="submit"
              disabled={sending}
              className="w-full bg-[#10b981] hover:bg-[#059669] disabled:opacity-60 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors"
              data-testid="partnership-submit"
            >
              {sending && <Loader2 className="w-4 h-4 animate-spin" />}
              {sending ? 'A enviar...' : 'Enviar pedido de parceria'}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
